import { inject, Injectable } from '@angular/core';
import type { Observable} from 'rxjs';
import { map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import config from '../../../config';
import { CycleManagementService } from './cycle-management.service';
import { WorkerMasterService } from './worker-master.service';
import type {
  ActiveCycleData,
  PastCycleData,
  UserCycleStatus
} from '../../shared/models/cycle-management.models';
import type { IUser } from '../../shared/models/user.model';

export interface SubManager {
  id: number;
  prid: string;
  email: string;
  first_name: string;
  last_name: string;
  profileUrl?: string;
  is_system_manager?: boolean; // Flag indicating if worker is registered as manager in BeFair for the specified market
}

@Injectable({
  providedIn: 'root'
})
export class ManagerDashboardService {
  private cycleManagementService = inject(CycleManagementService);
  private workerMasterService = inject(WorkerMasterService);
  private http = inject(HttpClient);
  private apiUrl = config.API_GATEWAY_ENV_ENDPOINT;

  /**
   * Get active cycles for the user
   */
  getActiveCycles(user: IUser, marketId?: number): Observable<ActiveCycleData[]> {
    return this.cycleManagementService.getCyclesByUserAndStatus(user.id, 'active', marketId).pipe(
      map(response => this.transformToActiveCycles(response.cycles))
    );
  }

  /**
   * Get paginated past cycles for the manager
   */
  getPaginatedPastCycles(
    userId: number,
    pageSize = 10,
    pageNumber = 0,
    marketId?: number
  ): Observable<{ cycles: PastCycleData[]; totalCount: number; pageSize: number; pageNumber: number; totalPages: number }> {
    return this.cycleManagementService.getPaginatedCyclesByUserAndStatus(userId, 'closed', pageSize, pageNumber, marketId)
      .pipe(
        map(response => ({
          cycles: this.transformToPastCycles(response.cycles),
          totalCount: response.total_count,
          pageSize: response.page_size,
          pageNumber: response.page_number,
          totalPages: response.total_pages
        }))
      );
  }


  /**
   * Transform API response to ActiveCycleData format
   */
  private transformToActiveCycles(cycles: UserCycleStatus[]): ActiveCycleData[] {
    return cycles.map(cycle => {
      const progress = cycle.total_forms > 0 ?
        Math.round((cycle.completed_assignments / cycle.total_forms) * 100) : 0;

      return {
        cycle_id: cycle.cycle_id,
        cycle_name: cycle.cycle_name,
        due_date: cycle.latest_deadline,
        progress,
        completed_forms: cycle.completed_assignments + cycle.submitted_assignments,
        completed_assignments: cycle.completed_assignments,
        total_forms: cycle.total_forms,
        status: 'active' as const,
        action_required_count: cycle.action_required_count
      };
    });
  }

  /**
   * Transform API response to PastCycleData format
   */
  private transformToPastCycles(cycles: UserCycleStatus[]): PastCycleData[] {
    return cycles.map(cycle => {
      const completionRate = cycle.total_forms > 0 ?
        Math.round((cycle.completed_assignments / cycle.total_forms) * 100) : 0;

      return {
        cycle_id: cycle.cycle_id,
        cycle_name: cycle.cycle_name,
        completed_date: cycle.cycle_default_deadline, // Using deadline as completed date
        completion_rate: completionRate,
        completed_forms: cycle.completed_assignments,
        completed_assignments: cycle.completed_assignments,
        total_forms: cycle.total_forms,
        status: 'closed' as const,
        action_required_count: cycle.action_required_count
      };
    });
  }


  /**
   * Get sub-managers from Worker Master API using manager hierarchy search
   * Returns all sub-managers with system registration status indicator
   */
  getSubManagers(managerPrid: string, marketId: number, skipLoader = false): Observable<SubManager[]> {
    return this.workerMasterService.getManagersByPrid(managerPrid, marketId, skipLoader).pipe(
      map(response => {
        // Transform workers array to SubManager format including registration status
        return (response.workers || [])
          .map(worker => ({
            id: parseInt(worker.workerId || worker.id || '0', 10),
            prid: worker.prid || '',
            email: worker.businessContact?.workEmail || '',
            first_name: worker.name?.firstName || '',
            last_name: worker.name?.lastName || '',
            is_system_manager: worker.is_system_manager === 'Y'
          }));
      })
    );
  }

  /**
   * Get sub-managers for a specific manager using dedicated API endpoint
   * Returns only system managers registered in BeFair for the specified market
   */
  getSubManagersFromAPI(managerPrid: string, marketId: number): Observable<SubManager[]> {
    const url = `${this.apiUrl}/managers/${managerPrid}/sub-managers?market_id=${marketId}`;

    return this.http.get<{ sub_managers: SubManager[] }>(url).pipe(
      map(response => response.sub_managers || [])
    );
  }
}
